import { useMemo, useRef } from 'react'

import { cn } from '@/utils'

import { TASK_ITEM_SELECTOR } from '@/features/richtext/nodes/taskitem/taskitem.utils'
import { TaskItemToggleContext } from '@/features/richtext/nodes/taskitem/TaskItemNodeView'
import { TIPTAP_CLASSES } from '@/features/richtext/tiptap.helpers'
import {
  createJSONContentFromHTMLContent,
  renderJSONContentAsReactNode,
} from '@/features/richtext/tiptap.helpers'

/**
 * Component that renders a cell's richtext content as a preview.
 * Does not depend on context.
 *
 * Pass `onTaskItemToggle` to make task item checkboxes clickable. The callback
 * receives the document-order index of the task item (see
 * `toggleTaskItemChecked`). Without it, checkboxes are `disabled`.
 * `taskItemsBlocked` stops toggling for now (see `TaskItemToggleContext`),
 * while keeping clicks on them from reaching the cell.
 * `taskItemsFocusable` puts the checkboxes in tab order.
 */
export function RichtextPreviewRenderer({
  contentAsHTML,
  className,
  onTaskItemToggle,
  taskItemsBlocked = null,
  taskItemsFocusable = false,
}: {
  contentAsHTML: string | object
  onTaskItemToggle?: (taskItemIndex: number) => void
  taskItemsBlocked?: 'pending' | 'error' | null
  taskItemsFocusable?: boolean
} & React.ComponentProps<'div'>) {
  const rootRef = useRef<HTMLDivElement>(null)

  // Same element on re-renders without content changes (selection, save
  // state), so React skips the tree and only updates context consumers
  const reactElement = useMemo(
    () =>
      contentAsHTML && typeof contentAsHTML === 'string'
        ? renderJSONContentAsReactNode(
            createJSONContentFromHTMLContent(contentAsHTML),
          )
        : null,
    [contentAsHTML],
  )

  if (reactElement === null) return null

  const getTaskItems = () =>
    Array.from(
      rootRef.current?.querySelectorAll<HTMLLIElement>(TASK_ITEM_SELECTOR) ??
        [],
    )

  const taskItemToggle = onTaskItemToggle
    ? {
        onToggle: (item: HTMLLIElement) => {
          const index = getTaskItems().indexOf(item)
          if (index !== -1) onTaskItemToggle(index)
        },
        focusSibling: (item: HTMLLIElement, delta: -1 | 1) => {
          const items = getTaskItems()
          const index = items.indexOf(item)
          if (index === -1) return

          items[index + delta]
            ?.querySelector<HTMLInputElement>('input[type="checkbox"]')
            ?.focus()
        },
        focusable: taskItemsFocusable,
        blocked: taskItemsBlocked,
      }
    : null

  return (
    <TaskItemToggleContext.Provider value={taskItemToggle}>
      <div
        ref={rootRef}
        role="none" // a11y purely structural
        className={cn(TIPTAP_CLASSES.BASE, TIPTAP_CLASSES.STATIC, className)}
      >
        {reactElement}
      </div>
    </TaskItemToggleContext.Provider>
  )
}
